import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  Tooltip,
} from "@mui/material";
import { statusStyleArray } from "../config";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  renderCurrencyIcon,
  renderCurrencyIconColored,
} from "../../../utils/CurrencyUtils";
import {
  VIEW_MODES,
  getRowCost,
  getRowKey,
  getScopeDisplayName,
  getDetailsRouteId,
  buildProjectDetailsHref,
} from "../utils/projectViewHelpers";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const formatDateOrDash = (val) => {
  if (val == null || val === "") return "-";
  const m = moment(val);
  if (!m.isValid()) return "-";
  return m.format("MMM DD, YYYY");
};

const ProjectTypePill = ({ managedBy }) => {
  const isContractor = String(managedBy || "").toLowerCase() === "contractor";
  const palette = isContractor
    ? { bg: "#FEF3C7", fg: "#92400E", label: "Contractor" }
    : { bg: "#EDE9FE", fg: "#5B21B6", label: "Reno" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 999,
        backgroundColor: palette.bg,
        color: palette.fg,
        fontFamily: "Inter Tight",
        whiteSpace: "nowrap",
      }}
    >
      {palette.label}
    </Box>
  );
};

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  textTransform: "capitalize",
}));

const TruncatedText = ({ text, maxWidth = 150 }) => {
  const display = text || "-";
  const truncated =
    display.length > 30 ? `${display.slice(0, 30)}...` : display;

  return (
    <Tooltip title={display} arrow>
      <p
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth,
          margin: 0,
          fontSize: "14px",
          fontWeight: 600,
          color: "#0D0D0E",
        }}
      >
        {truncated}
      </p>
    </Tooltip>
  );
};

export default function ProposalDetails({ details, viewMode = VIEW_MODES.PROJECTS }) {
  const navigate = useNavigate();
  const isScopesView = viewMode === VIEW_MODES.SCOPES;

  const handleRowClick = (item) => {
    const routeId = getDetailsRouteId(item, viewMode);
    if (!routeId) return;

    if (isScopesView) {
      navigate(
        buildProjectDetailsHref(routeId, {
          managedBy: item.parentManagedBy,
          projectId: item.parentProjectId,
          viewMode,
        }),
      );
      return;
    }

    navigate(
      buildProjectDetailsHref(routeId, {
        managedBy: item?.managed_by,
        projectId: item.id,
        viewMode,
      }),
    );
  };

  const renderNameCell = (item) => {
    const displayName = isScopesView ? getScopeDisplayName(item) : item?.name;

    return (
      <Box display="flex" alignItems="center">
        {!isScopesView &&
          item.project_image &&
          item.project_image[0]?.image && (
            <Box
              component="img"
              src={item.project_image[0].image}
              alt="Project Icon"
              sx={{
                width: 45,
                height: 45,
                borderRadius: "8px",
                border: "2px solid white",
                boxShadow: "0px 4px 2.67px 0px #00000026",
                marginRight: 1,
              }}
            />
          )}
        <div>
          <TruncatedText text={displayName} />
          <Typography
            sx={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              fontSize: "13px",
              fontWeight: 400,
              color: "#949DAA",
            }}
          >
            {item.project_type}
          </Typography>
        </div>
      </Box>
    );
  };

  const renderCostCell = (item) => {
    const cost = getRowCost(item);
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
        }}
      >
        {cost != null && renderCurrencyIconColored("black-small")}
        {cost != null ? formattedAmountWithoutAed(cost) : "-"}
      </Box>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        overflow: "none !important",
        boxShadow: "none",
        overflowY: "scroll",
        scrollbarWidth: "none",
        height: "100%",
      }}
    >
      <Table
        sx={{
          "& thead": {
            "& th": {
              color: "#949DAA",
              fontWeight: "500",
            },
          },
          "& tbody": {
            "& tr:hover": {
              backgroundColor: "#f7f7f7",
            },
            "& tr": {
              borderBottom: "none",
            },
          },
        }}
      >
        <TableHead>
          <TableRow>
            {isScopesView && (
              <StyledTableCell>Parent Project</StyledTableCell>
            )}
            <StyledTableCell>
              {isScopesView ? "Scope Name" : "Project Name"}
            </StyledTableCell>
            <StyledTableCell>Customer</StyledTableCell>
            <StyledTableCell>Location</StyledTableCell>
            <StyledTableCell>Start/ End Date</StyledTableCell>
            <StyledTableCell>Project Type</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
              <strong
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                Cost ({renderCurrencyIcon("small")})
              </strong>
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {details?.map((item) => {
            const isClickable = Boolean(getDetailsRouteId(item, viewMode));

            return (
              <TableRow
                key={getRowKey(item, viewMode)}
                onClick={() => isClickable && handleRowClick(item)}
                sx={{ cursor: isClickable ? "pointer" : "default" }}
              >
                {isScopesView && (
                  <StyledTableCell>
                    <TruncatedText
                      text={item.parentProjectName}
                      maxWidth={180}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell>{renderNameCell(item)}</StyledTableCell>

                <StyledTableCell>
                  <Box display="flex" alignItems="center">
                    {item?.user_data?.profile_url && (
                      <Box
                        component="img"
                        src={item.user_data.profile_url}
                        alt="Customer"
                        sx={{
                          width: 35,
                          height: 35,
                          borderRadius: "50%",
                          border: "2px solid white",
                          boxShadow: "0px 4px 2.67px 0px #00000026",
                          marginRight: 1,
                        }}
                      />
                    )}
                    {item?.user_data?.username}
                  </Box>
                </StyledTableCell>

                <StyledTableCell>
                  <Tooltip
                    title={item?.location || item?.location_address || ""}
                    arrow
                  >
                    <p
                      style={{
                        color:
                          item?.location || item?.location_address
                            ? "#0D0D0E"
                            : "#9CA3AF",
                        fontWeight: 500,
                        margin: 0,
                        maxWidth: 220,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.location || item?.location_address || "-"}
                    </p>
                  </Tooltip>
                </StyledTableCell>

                <StyledTableCell>
                  <p
                    style={{
                      color: "#949DAA",
                      marginBottom: "2px",
                      fontWeight: "500",
                    }}
                  >
                    {formatDateOrDash(item?.start_date)}
                  </p>
                  <p style={{ color: "#949DAA", fontWeight: "500" }}>
                    {formatDateOrDash(item?.end_date)}
                  </p>
                </StyledTableCell>

                <StyledTableCell>
                  <ProjectTypePill managedBy={item?.managed_by} />
                </StyledTableCell>

                <StyledTableCell>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      style={{
                        width: "15px",
                        height: "15px",
                        borderRadius: "50%",
                        backgroundColor:
                          statusStyleArray.find(
                            (style) => style.status === item?.status,
                          )?.backgroundColor || "transparent",
                        border: "3px solid #e5e5e5",
                      }}
                    />
                    <Typography
                      style={{
                        fontSize: "14px",
                        marginLeft: "8px",
                        color: "#0D0D0E",
                      }}
                    >
                      {getStatusDisplayText(item?.status)}
                    </Typography>
                  </div>
                </StyledTableCell>

                <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                  {renderCostCell(item)}
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
